import React from 'react';
import studentService from '../../services/api/student';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getArabicDate } from '../../services/utils/date';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Play, 
  Users, 
  BookOpen, 
  ShieldCheck, 
  Sparkles,
  ArrowRight,
  MonitorPlay,
  GraduationCap,
  CalendarDays
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const StudentSchedule = () => {
  const navigate = useNavigate();

  // جلب بيانات الجدول من الـ API
  const { data: scheduleData, isLoading, error } = useQuery(
    ['student-schedule'],
    () => studentService.getStudentSchedule().then(data => {
        return data;
    }),
    { retry: 1 }
  );

  const weekDays = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
  const timeSlots = [
    '08:00 - 09:00', '09:00 - 10:00', '10:00 - 11:00', '11:00 - 12:00',
    '12:00 - 13:00', '13:00 - 14:00', '14:00 - 15:00', '15:00 - 16:00',
    '16:00 - 17:00', '17:00 - 18:00'
  ];

  // Extract lessons from the response
  const lessons = scheduleData?.schedule || [];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحضير جدولك الدراسي...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center">
        <div className="bg-red-50 p-10 rounded-[2.5rem] border border-red-100">
          <CalendarIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">خطأ في عرض الجدول</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">نعتذر، لم نتمكن من مزامنة مواعيد دروسك حالياً. يرجى التحقق من اتصالك بالإنترنت.</p>
          <button onClick={() => window.location.reload()} className="bg-red-600 text-white px-10 py-3 rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg">
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <CalendarDays className="text-primary-600" size={32} />
            الجدول الزمني
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2">
            <ShieldCheck className="text-emerald-500" size={18} />
            نظرة شاملة على مواعيد محاضراتك ودروسك المباشرة
          </p>
        </div>
        
        <div className="bg-white p-2 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-2">
            <div className="bg-primary-50 px-5 py-2.5 rounded-2xl border border-gray-100 shadow-sm text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                <Sparkles size={14} className="text-primary-500" />
                اليوم: {getArabicDate(new Date())}
            </div>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { label: 'إجمالي الدروس المسجلة', value: lessons.length, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'الكورسات النشطة', value: new Set(lessons.map(lesson => lesson.course_title)).size, icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'المدرسين', value: new Set(lessons.map(lesson => lesson.teacher_name)).size, icon: GraduationCap, color: 'text-emerald-600', bg: 'bg-emerald-50' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
            <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>
              <stat.icon size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">{stat.label}</p>
              <p className="text-3xl font-black text-gray-900 leading-none">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Modern Schedule Grid Table */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group transition-all duration-500 hover:shadow-xl relative">
        <div className="overflow-x-auto">
          <div className="min-w-[1100px]">
            
            {/* Table Header (Days) */}
            <div className="grid grid-cols-8 bg-gray-50/50 border-b border-gray-100">
              <div className="px-6 py-6 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                الفترة الزمنية
              </div>
              {weekDays.map((day, index) => (
                <div key={index} className="px-6 py-6 text-center text-[10px] font-black text-gray-600 uppercase tracking-[0.2em] border-r border-gray-50/50">
                  {day}
                </div>
              ))}
            </div>

            {/* Timetable Body (Slots) */}
            <div className="divide-y divide-gray-50">
                {timeSlots.map((time, timeIndex) => (
                <div key={timeIndex} className="grid grid-cols-8 hover:bg-primary-50/10 transition-colors">
                    
                    {/* Time Column */}
                    <div className="px-4 py-8 text-center bg-gray-50/30 flex flex-col items-center justify-center border-b border-gray-50">
                        <div className="bg-white p-2 rounded-xl shadow-sm mb-2 text-primary-500">
                            <Clock size={16} />
                        </div>
                        <span className="text-[10px] font-black text-gray-900 tracking-tight">{time}</span>
                    </div>

                    {/* Day Columns */}
                    {weekDays.map((_, dayIndex) => {
                        // البحث عن الحصة في البيانات القادمة من الـ API
                        const slot = lessons?.find(s => s.day_of_week === dayIndex && s.time === time);
                        
                        return (
                            <div key={dayIndex} className="px-3 py-4 border-r border-gray-50 relative flex items-center justify-center min-h-[120px]">
                                {slot ? (
                                    <div className="w-full h-full bg-green-50/60 border border-green-100 rounded-[1.5rem] p-4 flex flex-col justify-between shadow-sm hover:shadow-md hover:bg-white hover:border-green-200 transition-all duration-300 group/slot cursor-pointer">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1.5 text-green-600 mb-1">
                                                <div className="p-1 bg-white rounded-lg shadow-sm">
                                                    <BookOpen size={12} />
                                                </div>
                                                <span className="text-[10px] font-black uppercase tracking-tighter truncate max-w-[80px]">
                                                    {slot.course_title}
                                                </span>
                                            </div>
                                            <p className="text-xs font-black text-green-900 leading-tight line-clamp-2">
                                                {slot.lesson_title}
                                            </p>
                                        </div>
                                        
                                        <div className="pt-3 border-t border-green-100/50 flex items-center justify-between">
                                            <div className="flex items-center gap-1 text-[9px] font-bold text-green-500">
                                                <Users size={10} />
                                                <span className="truncate max-w-[60px]">{slot.teacher_name}</span>
                                            </div>
                                            {slot.meeting_link && (
                                                <button
                                                    onClick={() => window.open(slot.meeting_link, '_blank')}
                                                    className="text-green-600 hover:text-green-700 transition-colors"
                                                    title="انضم للحصة"
                                                >
                                                    <Play size={10} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-1 opacity-20 group-hover:opacity-40 transition-opacity">
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">إجازة</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Info Card */}
      <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl group">
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
        <div className="relative z-10 flex flex-col h-full">
          <div className="bg-primary-600 w-10 h-10 rounded-xl flex items-center justify-center mb-4 shadow-xl">
            <MonitorPlay size={20} />
          </div>
          <h4 className="font-black text-lg mb-2">جدولك الدراسي</h4>
          <p className="text-gray-400 text-xs leading-relaxed font-medium mb-6">
            التزم بالحضور في الوقت المحدد لتحقيق أقصى استفادة من دروسك والمحافظة على مستواك التعليمي.
          </p>
          <button onClick={() => navigate('/student/courses')} className="mt-auto flex items-center gap-2 text-[10px] font-black text-primary-400 uppercase tracking-[0.2em] hover:text-white transition-colors">
            استعرض كورساتك <ArrowRight size={14} className="rotate-180" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentSchedule;